import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Send,
  Users,
  UserPlus,
  Pencil,
  ArrowDown,
  ImagePlus,
  X,
  ChevronLeft,
  MoreHorizontal,
} from "lucide-react";
import GifPickerButton from "./GifPickerButton";
import DmGifAttachment from "./DmGifAttachment";
import { useAuth } from "../../scripts/AuthContext";
import { useSocialDmSocket } from "../../scripts/SocialDmSocketContext";
import { useSocialDmThread } from "../../hooks/useSocialDmThread";
import { findSeenReceiptMessageId } from "@/lib/dmSeenReceipt";
import {
  addSocialDmMembers,
  clearSocialDmDraft,
  listSocialMutualFollows,
  loadSocialDmDraft,
  saveSocialDmDraft,
  updateSocialDmGroup,
} from "@/lib/socialDmApi";
import { sendSocialDmImage } from "@/lib/socialMedia";
import DmImageAttachment from "./DmImageAttachment";
import {
  MEDIA_IMAGE_MAX_BYTES,
  imageFilesFromPaste,
  isImageFile,
} from "@/lib/parseVideoEmbed";
import { toastError, toastSuccess } from "@/lib/toast";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import MarkdownBody from "../MarkdownBody";
import RichMessageEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMessageEditor";
import MobileMessageComposer from "./MobileMessageComposer";
import SocialDmPostCard from "./SocialDmPostCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSocialPaths } from "@/lib/socialPaths";
import { pinChatScrollToBottom } from "@/lib/pinChatScrollToBottom";
import { getMessageClusterMeta } from "@/lib/dmMessageCluster";

const NEAR_BOTTOM_PX = 80;

export default function SocialDmThread({
  conversationId,
  conversation = null,
  onConversationChange,
  onBack,
  className,
}) {
  const { accessToken } = useAuth();
  const isMobile = useIsMobile();
  const { emitTyping, subscribe } = useSocialDmSocket();
  const paths = useSocialPaths();
  const {
    messages,
    loading,
    sending,
    hasMoreOlder,
    currentUserId,
    peerLastReadAt,
    loadOlder,
    sendMessage,
    refresh,
    setMessages,
  } = useSocialDmThread(conversationId);

  const seenReceiptMessageId = useMemo(
    () =>
      conversation?.type === "group"
        ? null
        : findSeenReceiptMessageId(messages, currentUserId, peerLastReadAt),
    [conversation?.type, messages, currentUserId, peerLastReadAt]
  );

  const [draft, setDraft] = useState("");
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState(null);
  const [pendingGif, setPendingGif] = useState(null);
  const [sendingImage, setSendingImage] = useState(false);
  const imageInputRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState("");
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [mutualFollows, setMutualFollows] = useState([]);
  const [selectedAddIds, setSelectedAddIds] = useState([]);
  const [membersBusy, setMembersBusy] = useState(false);
  const [typingUsers, setTypingUsers] = useState(() => new Map());

  const scrollRef = useRef(null);
  const listContentRef = useRef(null);
  const composerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);

  const isGroup = conversation?.type === "group";
  const peer = conversation?.peer;
  const members = useMemo(
    () => conversation?.members || [],
    [conversation?.members]
  );
  const headerTitle = isGroup
    ? conversation?.title || "Group"
    : peer?.display_name || peer?.username || "Direct message";
  const peerHandle = peer?.username
    ? String(peer.username).replace(/^@+/, "")
    : null;
  const peerIsOrg = Boolean(peer?.is_org);
  const peerHref = peerHandle
    ? peerIsOrg
      ? paths.org(peerHandle)
      : paths.user(peerHandle)
    : null;

  useEffect(() => {
    if (conversationId == null) return;
    setDraft(loadSocialDmDraft(conversationId));
    setTypingUsers(new Map());
    setPendingImageFile(null);
    stickToBottomRef.current = true;
    setShowScrollDown(false);
  }, [conversationId]);

  useEffect(() => {
    if (!pendingImageFile) {
      setPendingImagePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pendingImageFile);
    setPendingImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImageFile]);

  useEffect(() => {
    if (conversationId == null) return;
    saveSocialDmDraft(conversationId, draft);
  }, [conversationId, draft]);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    setShowScrollDown(false);
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      pinChatScrollToBottom(el);
    }
  }, []);

  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom(false);
  }, [conversationId, messages, loading, scrollToBottom]);

  useEffect(() => {
    const el = scrollRef.current;
    const content = listContentRef.current;
    if (!el || !content || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [conversationId]);

  useEffect(() => {
    return subscribe((event, payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (event === "social-dm.typing") {
        const uid = payload?.user_id;
        if (!uid || String(uid) === String(currentUserId)) return;
        setTypingUsers((prev) => {
          const next = new Map(prev);
          if (payload.typing) {
            next.set(uid, Date.now() + 4000);
          } else {
            next.delete(uid);
          }
          return next;
        });
        return;
      }
      if (event === "social-dm.message") {
        const msg =
          payload?.message ?? (payload?.message_id ? payload : null);
        const senderId = msg?.sender_id;
        if (senderId == null) return;
        setTypingUsers((prev) => {
          let key = null;
          for (const k of prev.keys()) {
            if (String(k) === String(senderId)) {
              key = k;
              break;
            }
          }
          if (key == null) return prev;
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      }
    });
  }, [subscribe, conversationId, currentUserId]);

  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        if (prev.size === 0) return prev;
        const now = Date.now();
        let changed = false;
        const next = new Map();
        for (const [uid, exp] of prev) {
          if (exp > now) next.set(uid, exp);
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const typingLabel = useMemo(() => {
    const ids = [...typingUsers.keys()].filter(
      (id) => String(id) !== String(currentUserId)
    );
    if (ids.length === 0) return null;
    const names = ids.map((id) => {
      const m = members.find((x) => String(x.user_id) === String(id));
      if (m) return m.display_name || m.username || "Someone";
      if (peer && String(peer.user_id) === String(id)) {
        return peer.display_name || peer.username || "Someone";
      }
      return "Someone";
    });
    if (names.length === 1) return `${names[0]} is typing…`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
    return `${names[0]} and ${names.length - 1} others are typing…`;
  }, [typingUsers, currentUserId, members, peer]);

  const referenceContext = useMemo(
    () =>
      accessToken && conversationId
        ? {
            enabled: true,
            socialMode: true,
            accessToken,
            conversationId,
          }
        : null,
    [accessToken, conversationId]
  );

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const prevTop = lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;
    if (
      isMobile &&
      prevTop - scrollTop > 12 &&
      document.activeElement?.closest?.("[contenteditable]")
    ) {
      composerRef.current?.blur?.();
    }
    const nearBottom =
      el.scrollHeight - scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    stickToBottomRef.current = nearBottom;
    setShowScrollDown(!nearBottom);
    if (scrollTop < 80 && hasMoreOlder) loadOlder();
  };

  const emitTypingDebounced = useCallback(
    (typing) => {
      if (conversationId == null) return;
      emitTyping(conversationId, typing);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (typing) {
        typingTimerRef.current = setTimeout(() => {
          emitTyping(conversationId, false);
        }, 2500);
      }
    },
    [conversationId, emitTyping]
  );

  const handleSend = async () => {
    const content = normalizeEditorMarkdown(
      composerRef.current?.getMarkdown?.() ?? draft
    );
    if (pendingGif) {
      if (sending) return;
      try {
        await sendMessage({
          content: isMarkdownEmpty(content) ? undefined : content,
          gif: pendingGif,
        });
        setDraft("");
        setPendingGif(null);
        setPendingImageFile(null);
        clearSocialDmDraft(conversationId);
        composerRef.current?.clear?.();
        emitTyping(conversationId, false);
      } catch (err) {
        toastError(err, "Could not send GIF");
      }
      return;
    }
    if (pendingImageFile) {
      if (sendingImage) return;
      setSendingImage(true);
      try {
        const msg = await sendSocialDmImage(
          accessToken,
          conversationId,
          pendingImageFile,
          isMarkdownEmpty(content) ? undefined : content
        );
        setDraft("");
        setPendingImageFile(null);
        clearSocialDmDraft(conversationId);
        composerRef.current?.clear?.();
        emitTyping(conversationId, false);
        if (msg?.message_id) {
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.message_id === msg.message_id);
            if (idx === -1) return [...prev, msg];
            const next = [...prev];
            next[idx] = msg;
            return next;
          });
        } else {
          await refresh();
        }
      } catch (err) {
        toastError(err, "Could not send image");
      } finally {
        setSendingImage(false);
      }
      return;
    }
    if (isMarkdownEmpty(content) || sending) return;
    try {
      await sendMessage({ content });
      setDraft("");
      clearSocialDmDraft(conversationId);
      composerRef.current?.clear?.();
      emitTyping(conversationId, false);
    } catch (err) {
      toastError(err, "Could not send message");
    }
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isImageFile(file)) {
      toastError("Only image uploads are allowed");
      return;
    }
    if (file.size > MEDIA_IMAGE_MAX_BYTES) {
      toastError("Image exceeds 10MB limit");
      return;
    }
    setPendingImageFile(file);
  };

  const onComposerPaste = (e) => {
    const images = imageFilesFromPaste(e.nativeEvent || e);
    if (!images.length) return;
    setPendingImageFile(images[0]);
  };

  const openRename = () => {
    setRenameTitle(conversation?.title || "");
    setRenameOpen(true);
  };

  const handleRename = async () => {
    if (!accessToken || !conversationId) return;
    try {
      const updated = await updateSocialDmGroup(accessToken, conversationId, {
        title: renameTitle.trim(),
      });
      toastSuccess("Group renamed");
      setRenameOpen(false);
      onConversationChange?.(updated);
    } catch (err) {
      toastError(err, "Could not rename group");
    }
  };

  useEffect(() => {
    if (!addMembersOpen || !accessToken) return;
    let cancelled = false;
    const q = String(memberQuery || "")
      .trim()
      .replace(/^@+/, "");
    const timer = setTimeout(async () => {
      try {
        const users = await listSocialMutualFollows(accessToken, q);
        if (!cancelled) setMutualFollows(users || []);
      } catch (err) {
        console.error("Failed to load mutual follows:", err);
      }
    }, q ? 200 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [addMembersOpen, accessToken, memberQuery]);

  const existingMemberIds = useMemo(
    () => new Set((conversation?.members || []).map((m) => m.user_id)),
    [conversation?.members]
  );

  const handleAddMembers = async () => {
    if (!accessToken || !conversationId || !selectedAddIds.length) return;
    setMembersBusy(true);
    try {
      const updated = await addSocialDmMembers(
        accessToken,
        conversationId,
        selectedAddIds
      );
      toastSuccess("Members added");
      setAddMembersOpen(false);
      setSelectedAddIds([]);
      setMemberQuery("");
      onConversationChange?.(updated);
    } catch (err) {
      toastError(err, "Could not add members");
    } finally {
      setMembersBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col min-h-0 min-w-0 h-full relative overflow-x-hidden",
        className
      )}
    >
      <header
        className={cn(
          "shrink-0 flex items-center justify-between gap-3 px-3 sm:px-5 border-b border-[hsl(var(--sm-divider))] bg-[hsl(var(--sm-panel)/0.55)] backdrop-blur-sm min-w-0",
          isMobile ? "py-2" : "py-3.5"
        )}
      >
        <div className="min-w-0 flex items-center gap-2 sm:gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="md:hidden shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-[hsl(var(--sm-bubble-in))]"
              aria-label="Back to conversations"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          ) : null}
          {isGroup ? (
            <div
              className={cn(
                "flex items-center justify-center rounded-full bg-[hsl(var(--sm-bubble-in))] shrink-0",
                isMobile ? "h-8 w-8" : "h-11 w-11"
              )}
            >
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
          ) : peerIsOrg ? (
            <OrganizationAvatar
              org={{
                name: peer?.display_name,
                avatar_url: peer?.avatar_url,
              }}
              className={cn("shrink-0", isMobile ? "h-8 w-8" : "h-11 w-11")}
            />
          ) : (
            <SocialUserAvatar
              user={peer}
              avatarUrl={peer?.avatar_url}
              displayName={peer?.display_name}
              className={cn("shrink-0", isMobile ? "h-8 w-8" : "h-11 w-11")}
            />
          )}
          <div className="min-w-0 flex-1">
            {peerHref && !isGroup ? (
              <Link
                to={peerHref}
                className="text-[15px] font-semibold truncate hover:underline block"
                title={headerTitle}
              >
                {headerTitle}
              </Link>
            ) : (
              <h2 className="text-[15px] font-semibold truncate" title={headerTitle}>
                {headerTitle}
              </h2>
            )}
            {!isMobile && isGroup ? (
              <p className="text-xs text-muted-foreground truncate">
                {conversation?.members?.length || 0} members
              </p>
            ) : null}
            {!isMobile && !isGroup && peerHandle ? (
              <p className="text-xs italic text-muted-foreground truncate" title={`@${peerHandle}`}>
                @{peerHandle}
              </p>
            ) : null}
          </div>
        </div>
        {isMobile && (isGroup || peerHandle) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full h-9 w-9 p-0 shrink-0"
                aria-label="Conversation actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isGroup ? (
                <DropdownMenuItem
                  disabled
                  className="text-xs text-muted-foreground focus:bg-transparent"
                >
                  {conversation?.members?.length || 0} members
                </DropdownMenuItem>
              ) : peerHandle ? (
                <DropdownMenuItem
                  disabled
                  className="text-xs text-muted-foreground focus:bg-transparent"
                >
                  @{peerHandle}
                </DropdownMenuItem>
              ) : null}
              {isGroup ? (
                <>
                  <DropdownMenuItem onClick={() => setAddMembersOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add members
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openRename}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Rename group
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : isGroup ? (
          <div className="flex items-center gap-0.5 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full h-9 w-9 p-0"
              onClick={() => setAddMembersOpen(true)}
              title="Add members"
            >
              <UserPlus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full h-9 w-9 p-0"
              onClick={openRename}
              title="Rename group"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-4 py-4"
      >
        <div
          ref={listContentRef}
          className={cn(
            "min-w-0 w-full",
            isMobile ? "space-y-1" : "space-y-3.5"
          )}
        >
        {loading && messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Loading messages…
          </p>
        ) : null}
        {messages.map((msg, msgIndex) => {
          const mine = msg.sender_id === currentUserId;
          const sender = msg.sender || {};
          const cluster = getMessageClusterMeta(messages, msgIndex);
          const showIncomingAvatar =
            !mine &&
            (!isMobile || isGroup) &&
            (!isMobile || cluster.startOfRun);
          const reserveAvatarSlot =
            !mine && isMobile && isGroup && !cluster.startOfRun;
          return (
            <div
              key={msg.message_id}
              className={cn(
                "sm-msg-appear flex w-full min-w-0 gap-2",
                mine ? "justify-end" : "justify-start",
                isMobile && cluster.startOfRun && "mt-2"
              )}
            >
              {showIncomingAvatar ? (
                sender?.is_org ? (
                  <OrganizationAvatar
                    org={{
                      name: sender.display_name,
                      avatar_url: sender.avatar_url,
                    }}
                    className={cn(
                      "shrink-0 mt-0.5",
                      isMobile ? "h-7 w-7" : "h-8 w-8"
                    )}
                    fallbackClassName="text-[10px]"
                  />
                ) : (
                  <SocialUserAvatar
                    user={sender}
                    avatarUrl={sender.avatar_url}
                    displayName={sender.display_name}
                    className={cn(
                      "shrink-0 mt-0.5",
                      isMobile ? "h-7 w-7" : "h-8 w-8"
                    )}
                    fallbackClassName="text-[10px]"
                  />
                )
              ) : reserveAvatarSlot ? (
                <div className="w-7 shrink-0" aria-hidden />
              ) : null}
              <div
                className={cn(
                  "max-w-[min(85%,28rem)] min-w-0 flex flex-col overflow-x-hidden",
                  mine ? "items-end" : "items-start"
                )}
              >
              <div
                className={cn(
                  "rounded-[1.25rem] px-3.5 py-2.5 text-sm space-y-2 min-w-0 max-w-full break-words overflow-x-hidden",
                  mine ? "sm-bubble-out rounded-br-md" : "sm-bubble-in rounded-bl-md"
                )}
              >
                {!mine && isGroup && (!isMobile || cluster.startOfRun) ? (
                  <p className="text-[11px] font-medium text-muted-foreground truncate">
                    {sender.display_name || sender.username || "User"}
                  </p>
                ) : null}
                {msg.social_post || msg.social_post_id ? (
                  <SocialDmPostCard
                    post={
                      msg.social_post || { social_post_id: msg.social_post_id }
                    }
                    inverse={mine}
                    compact
                  />
                ) : null}
                {msg.content ? (
                  <MarkdownBody
                    content={msg.content}
                    className="prose prose-sm max-w-none"
                  />
                ) : null}
                {Array.isArray(msg.media) && msg.media[0]?.type === "image" ? (
                  <DmImageAttachment
                    attachment={msg.media[0]}
                    className="max-w-full max-h-72"
                  />
                ) : null}
                {Array.isArray(msg.media) && msg.media[0]?.type === "gif" ? (
                  <DmGifAttachment attachment={msg.media[0]} />
                ) : null}
              </div>
              {mine && seenReceiptMessageId === msg.message_id ? (
                <p className="text-[10px] text-muted-foreground mt-0.5 px-1">
                  Seen
                </p>
              ) : null}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {showScrollDown ? (
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-10">
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="rounded-full h-9 w-9 shadow-md"
            onClick={() => scrollToBottom(true)}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      ) : null}

      {typingLabel ? (
        <p className="text-xs text-muted-foreground px-4 pt-1 pb-0 shrink-0 truncate">
          {typingLabel}
        </p>
      ) : null}

      <footer className="shrink-0 min-w-0 overflow-x-hidden px-3 sm:px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom,0px))] md:pb-4">
        {pendingGif ? (
          <div className="mb-2 flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs bg-[hsl(var(--sm-bubble-in))]">
            <DmGifAttachment attachment={pendingGif} />
            <button
              type="button"
              className="p-0.5 hover:opacity-70 ml-auto"
              onClick={() => setPendingGif(null)}
              aria-label="Remove GIF"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        {pendingImageFile ? (
          <div className="mb-2 flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs bg-[hsl(var(--sm-bubble-in))]">
            {pendingImagePreviewUrl ? (
              <img
                src={pendingImagePreviewUrl}
                alt=""
                className="h-24 w-24 rounded-lg object-cover shrink-0"
              />
            ) : (
              <ImagePlus className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="truncate flex-1">{pendingImageFile.name}</span>
            <button
              type="button"
              className="p-0.5 hover:opacity-70"
              onClick={() => setPendingImageFile(null)}
              aria-label="Remove image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        <input
          ref={imageInputRef}
          type="file"
          className="hidden"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={onPickImage}
        />
        {isMobile ? (
          <MobileMessageComposer
            composerRef={composerRef}
            value={draft}
            onChange={(next) => {
              setDraft(next);
              emitTypingDebounced(!isMarkdownEmpty(next));
            }}
            onSubmit={handleSend}
            onPaste={onComposerPaste}
            disabled={sending || sendingImage}
            sendDisabled={sending || sendingImage}
            placeholder="Write a message…"
            referenceContext={referenceContext}
            social
            onAttach={() => imageInputRef.current?.click()}
            attachDisabled={sending || sendingImage || !!pendingGif}
            onGifSelect={(gif) => {
              setPendingImageFile(null);
              setPendingGif(gif);
            }}
            gifDisabled={sending || sendingImage || !!pendingImageFile}
            onEmojiInsert={(native) =>
              composerRef.current?.insertText(native)
            }
          />
        ) : (
          <div className="sm-composer flex min-w-0 flex-wrap sm:flex-nowrap items-end gap-1.5 sm:gap-2 rounded-[1.5rem] border px-2 sm:px-3 py-2.5">
            <button
              type="button"
              className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-[hsl(var(--sm-bubble-in))] disabled:pointer-events-none disabled:opacity-50 md:h-9 md:w-9"
              onClick={() => imageInputRef.current?.click()}
              disabled={sending || sendingImage || !!pendingGif}
              title="Attach image"
              aria-label="Attach image"
            >
              <ImagePlus className="h-4 w-4" />
            </button>
            <GifPickerButton
              social
              className="h-10 w-10 md:h-8 md:w-8"
              disabled={sending || sendingImage || !!pendingImageFile}
              onSelect={(gif) => {
                setPendingImageFile(null);
                setPendingGif(gif);
              }}
            />
            <div
              className="flex-1 min-w-0 px-1 py-0.5"
              onPaste={onComposerPaste}
            >
              <RichMessageEditor
                ref={composerRef}
                value={draft}
                onChange={(next) => {
                  setDraft(next);
                  emitTypingDebounced(!isMarkdownEmpty(next));
                }}
                onSubmit={handleSend}
                placeholder="Write a message…"
                referenceContext={referenceContext}
                disabled={sending}
              />
            </div>
            <button
              type="button"
              className="sm-send shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full disabled:pointer-events-none"
              disabled={sending || sendingImage}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSend}
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        )}
      </footer>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Rename group</DialogTitle>
          </DialogHeader>
          <Input
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            placeholder="Group name"
            autoFocus
            className="rounded-xl"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => setRenameOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="sm-send rounded-full border-0"
              onClick={handleRename}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addMembersOpen} onOpenChange={setAddMembersOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add members</DialogTitle>
          </DialogHeader>
          <Input
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            placeholder="Search by name or @handle…"
            className="rounded-xl"
          />
          <div className="max-h-56 overflow-y-auto rounded-xl border border-[hsl(var(--sm-divider))] divide-y divide-[hsl(var(--sm-divider))]">
            {(mutualFollows || [])
              .filter((u) => !existingMemberIds.has(u.user_id))
              .filter((u) => {
                const q = String(memberQuery || "")
                  .trim()
                  .toLowerCase()
                  .replace(/^@+/, "");
                if (!q) return true;
                const name = String(u.display_name || "").toLowerCase();
                const handle = String(u.username || "")
                  .toLowerCase()
                  .replace(/^@+/, "");
                return name.includes(q) || handle.includes(q);
              })
              .map((u) => {
                const selected = selectedAddIds.includes(u.user_id);
                const handle = u.username
                  ? String(u.username).replace(/^@+/, "")
                  : null;
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    className={cn(
                      "w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors",
                      selected
                        ? "bg-[hsl(var(--sm-accent)/0.12)]"
                        : "hover:bg-[hsl(var(--sm-accent)/0.06)]"
                    )}
                    onClick={() =>
                      setSelectedAddIds((prev) =>
                        prev.includes(u.user_id)
                          ? prev.filter((id) => id !== u.user_id)
                          : [...prev, u.user_id]
                      )
                    }
                  >
                    <span className="min-w-0 flex items-center gap-2">
                      <SocialUserAvatar
                        user={u}
                        avatarUrl={u.avatar_url}
                        displayName={u.display_name}
                        className="h-8 w-8 shrink-0"
                        fallbackClassName="text-[10px]"
                      />
                      <span className="min-w-0 flex items-baseline gap-1.5">
                        <span className="font-medium truncate">
                          {u.display_name || u.username}
                        </span>
                        {handle ? (
                          <span className="min-w-0 truncate text-xs italic text-muted-foreground">
                            @{handle}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {selected ? "Selected" : "Add"}
                    </span>
                  </button>
                );
              })}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => setAddMembersOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="sm-send rounded-full border-0"
              disabled={!selectedAddIds.length || membersBusy}
              onClick={handleAddMembers}
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
